import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Clock } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface PontoAberto {
  id: number;
  nome: string;
  /** 'HH:MM' da entrada que ficou sem par. */
  desde: string;
  /** 'HH:MM' do fim do expediente da pessoa. */
  fim: string | null;
  minutos_alem: number;
  recebe_chamados: boolean;
}

const POLLING_MS = 5 * 60 * 1000;

/**
 * Disparado pelo modal que lanca batida (tela Equipe) pra esta faixa se reler NA HORA.
 *
 * Sem isto a faixa fica ate 5 minutos afirmando "LANNA passou do horario" DEPOIS do ponto dela
 * ter sido fechado — e' a familia da [DA-684]: a faixa tem carregamento proprio e nao e'
 * alcancada pelo `carregar()` da tela que gravou. O modo de falhar e' o pior, porque a tela se
 * contradiz com o proprio aviso de sucesso e o reflexo certo de quem opera e' repetir o gesto.
 * Visto ao vivo em 08/09/2026: o print da verificacao pegou a faixa ainda acesa 13 segundos
 * depois de o ponto ter sido fechado.
 */
export const EVENTO_PONTO_ALTERADO = 'fiodecobre:ponto-alterado';
// Ambar solido + texto preto (feedback_alertas_fundo_solido / user_daltonismo). Fixo, nunca
// variavel de tema: selo que pinta fundo E fonte com token nao garante contraste entre os dois
// ([DA-678]).
const FUNDO = '#fbbf24';
const BORDA = '#b45309';
const TEXTO = '#000';

function quanto(min: number): string {
  if (min < 60) return `ha ${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `ha ${h}h` : `ha ${h}h${String(m).padStart(2, '0')}`;
}

// Ponto ABERTO depois do fim do expediente da pessoa — pedido dele em 08/09/2026: *"Gostaria que
// ponto fora de hora gerasse uma pendencia para Administrador"* ([DA-690]).
//
// Por que a faixa existe, e nao so a coluna da Equipe: o scheduler que fecha o ponto esquecido so
// roda as **23h**. Quem tem expediente ate as 11h fica 12 horas com o ponto aberto, e ponto aberto
// e' o que poe a pessoa no rodizio de chamados ([DA-019]/[DA-463]) — cliente escrevendo pra loja
// cai no nome de quem foi embora. Era o caso da LANNA no dia em que isto nasceu.
//
// ⛔ Isto NAO grava `ponto_anomalias`: anomalia conta contra o bonus semanal da pessoa, e a saida
// esquecida JA gera uma quando o scheduler fecha. Quem precisa agir e' o admin, entao o aviso vem
// pra ele e nao vira advertencia pra ela.
export function AlertaPontoAbertoBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [itens, setItens] = useState<PontoAberto[]>([]);
  const [aberto, setAberto] = useState(false);
  // Contador nas dependencias do efeito, e nao `key`: remontar jogaria fora o estado de
  // expandido/recolhido que a pessoa acabou de escolher ([DA-684]).
  const [recarga, setRecarga] = useState(0);

  const podeVer = usuario && ['admin', 'gerente'].includes(usuario.perfil);

  useEffect(() => {
    const aoAlterar = () => setRecarga((n) => n + 1);
    window.addEventListener(EVENTO_PONTO_ALTERADO, aoAlterar);
    return () => window.removeEventListener(EVENTO_PONTO_ALTERADO, aoAlterar);
  }, []);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<{ itens: PontoAberto[] }>('/api/ponto/abertos-fora-de-hora');
        if (!cancelado) setItens(r.itens);
      } catch { /* silencia */ }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer, recarga]);

  const ativo = !!podeVer && itens.length > 0;
  const recolhido = useAlertaFaixa(
    'ponto-aberto',
    ativo ? { rotulo: 'PONTO ABERTO', bg: FUNDO, borda: BORDA, qtd: itens.length } : null,
  );

  if (!ativo || recolhido) return null;

  const noRodizio = itens.filter((i) => i.recebe_chamados).length;
  const maisAntigo = itens.reduce((m, i) => Math.max(m, i.minutos_alem), 0);

  return (
    <div style={{ background: FUNDO, borderLeft: `4px solid ${BORDA}`, color: TEXTO, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px' }}>
        <button
          type="button"
          onClick={() => setAberto((o) => !o)}
          title={aberto ? 'Recolher' : 'Ver quem esta com o ponto aberto'}
          style={{
            background: 'rgba(0,0,0,0.18)', color: TEXTO, border: 'none', borderRadius: 6, cursor: 'pointer',
            width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {aberto ? <Minus size={15} /> : <Plus size={15} />}
        </button>
        <Clock size={16} style={{ flexShrink: 0 }} />
        <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>PONTO ABERTO</span>
        <span>
          {itens.length === 1
            ? `${itens[0]!.nome} passou do horario (${itens[0]!.fim}) e nao bateu a saida — ${quanto(itens[0]!.minutos_alem)}`
            : `${itens.length} pessoas passaram do horario sem bater a saida (a mais antiga ${quanto(maisAntigo)})`}
          {noRodizio > 0 && (
            <b> — {noRodizio === 1 ? 'esta' : 'estao'} recebendo chamado de cliente sem estar na loja.</b>
          )}
          {' '}O sistema so fecha sozinho as 23h.
        </span>
        <button
          type="button"
          onClick={() => navigate('/equipe')}
          style={{
            marginLeft: 'auto', flexShrink: 0, background: 'rgba(255,255,255,0.85)', color: '#000',
            border: '1px solid rgba(0,0,0,0.2)', borderRadius: 6, padding: '3px 10px',
            fontWeight: 700, fontSize: 12, cursor: 'pointer',
          }}
        >
          Fechar na Equipe
        </button>
      </div>

      {aberto && (
        <div style={{ padding: '0 14px 10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {itens.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => navigate('/equipe')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                background: 'rgba(255,255,255,0.80)', border: '1px solid rgba(0,0,0,0.15)',
                borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#000',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{p.nome}</div>
                <div style={{ fontSize: 11 }}>
                  entrou <b>{p.desde}</b> · devia sair <b>{p.fim}</b> · aberto {quanto(p.minutos_alem)}
                  {p.recebe_chamados && ' · no rodizio de chamados'}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
